import { apiPostV2, Failure, getBaseUrl } from '../../support/apiCallHelper';
import { setSessionData } from './gatSessionSlice';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { CANCEL_MISSION, SCHEDULE_MISSION } from '../../support/apiConstants';

export interface CustomerResponse {
  firstName: string;
  lastName: string;
  address: string;
  zipCode: string;
  city: string;
}

export interface CustomerVehicleResponse {
  registrationNumber: string;
  make: string;
  brand: string;
  model: string;
  year: number;
}

export interface SelfServeResponse {
  customer: CustomerResponse;
  customerVehicle: CustomerVehicleResponse | null;
  timeSlots: {
    start: string;
    end: string;
    isAvailable: boolean;
  }[];
  orders: any[];
}

export const createGatMission = createAsyncThunk('gat/createMission', async (_, { dispatch }) => {
  const baseUrl = getBaseUrl();
  const appId = process.env.REACT_APP_APPLICATION_ID!;
  // const appId = 'gat-rescue-frontend';
  const url = `${baseUrl}v1/missions,create-by-customer`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'gat-application-id': appId,
    },
  });

  if (!response.ok) {
    throw new Error(`Error creating mission: ${response.statusText}`);
  }

  const data = await response.json();

  dispatch(
    setSessionData({
      missionId: data.id,
      accessToken: data.accessToken,
      token: data.token,
    }),
  );

  /* localStorage.setItem('missionId', data.id);
  localStorage.setItem('accessToken', data.accessToken);
  localStorage.setItem('token', data.token);*/

  return data;
});

export const createGatSelfServe = createAsyncThunk(
  'gat/selfServe',
  async ({
    missionId,
    token,
    customerType,
    customerCode,
    registrationNumber,
    controller,
    missionDate,
    source,
    destination,
    email,
    phone,
    firstName,
    lastName,
  }: {
    missionId: string | null;
    token: string | null;
    customerCode?: string;
    registrationNumber?: string;
    customerType?: string;
    controller?: AbortController;
    missionDate?: string;
    source?: {
      latitude: number;
      longitude: number;
      city?: string;
      zipCode?: string;
      address?: string;
    };
    destination?: {
      latitude: number;
      longitude: number;
      city?: string;
      zipCode?: string;
      address?: string;
    };
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
  }) => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}v2/missions,self-serve-transportation`;

    const body: Record<string, any> = {
      missionId,
      token,
    };

    if (customerCode) body.customerCode = customerCode;
    if (registrationNumber) body.registrationNumber = registrationNumber;
    if (customerType)
      body.customerType = {
        state: 'Modified',
        value: customerType,
      };
    if (missionDate)
      body.missionDate = {
        state: 'Modified',
        value: missionDate,
      };
    if (source?.latitude)
      body.sourceLatitude = {
        state: 'Modified',
        value: source.latitude,
      };
    if (source?.longitude)
      body.sourceLongitude = {
        state: 'Modified',
        value: source.longitude,
      };
    if (destination?.latitude)
      body.destinationLatitude = {
        state: 'Modified',
        value: destination.latitude,
      };
    if (destination?.longitude)
      body.destinationLongitude = {
        state: 'Modified',
        value: destination.longitude,
      };
    if (destination?.address) {
      body.destinationAddress = {
        state: 'Modified',
        value: destination.address,
      };
    }
    if (source?.address) {
      body.sourceAddress = {
        state: 'Modified',
        value: source.address,
      };
    }
    if (destination?.zipCode) {
      body.destinationZipCode = {
        state: 'Modified',
        value: destination.zipCode,
      };
    }
    if (source?.zipCode) {
      body.sourceZipCode = {
        state: 'Modified',
        value: source.zipCode,
      };
    }
    if (destination?.city) {
      body.destinationCity = {
        state: 'Modified',
        value: destination.city,
      };
    }
    if (source?.city) {
      body.sourceCity = {
        state: 'Modified',
        value: source.city,
      };
    }
    if (email) {
      body.email = {
        state: 'Modified',
        value: email,
      };
    }
    if (phone) {
      body.mobilePhone = {
        state: 'Modified',
        value: phone,
      };
    }
    if (firstName) {
      body.firstName = {
        state: 'Modified',
        value: firstName,
      };
    }
    if (lastName) {
      body.lastName = {
        state: 'Modified',
        value: lastName,
      };
    }

    const response = await apiPostV2<SelfServeResponse>(url, body, controller);

    if (response instanceof Failure) {
      throw new Error(`Error calling self-serve: ${response}`);
    }

    return response;
  },
);

export const scheduleMissionByToken = createAsyncThunk(
  'missions/scheduleByToken',
  async ({
    missionDate,
    missionId,
    token,
    controller,
  }: {
    missionDate: string;
    missionId: string;
    token: string;
    controller?: AbortController;
  }) => {
    const baseUrl = getBaseUrl();
    const url = `${baseUrl}${SCHEDULE_MISSION}`;

    const body = {
      missionDate,
      missionId,
      token,
    };

    const response = await apiPostV2(url, body, controller);

    if (response instanceof Failure) {
      throw new Error(`Error scheduling mission: ${response}`);
    }

    return response;
  },
);

export async function cancelScheduledMission({
  token,
  missionId,
  controller,
}: {
  token: string;
  missionId: string;
  controller?: AbortController;
}): Promise<unknown> {
  const url = `${getBaseUrl()}${CANCEL_MISSION}`;

  const body = {
    token,
    missionId,
    cancelReasonChoiceId: '',
    reason: '',
  };

  try {
    const result = await apiPostV2(url, body, controller);
    return result;
  } catch (error: any) {
    if (error instanceof SyntaxError && error.message.includes('Unexpected end of JSON input')) {
      return null;
    }

    throw error;
  }
}
